//Oct10 BasicSettings.jsx:
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Alert, Box, TextField, Typography, MenuItem, FormControl, InputLabel, Select, Switch, FormControlLabel, Tooltip } from '@mui/material';
import { useFieldEdit } from '../../../../contexts/FieldEditContext';
import { useFormContext } from '../../../../contexts/FormContext';
import { useAuth } from '../../../../contexts/AuthContext';

import FormFieldCustomSelectComponent from '../../FormFieldCustomSelectComponent';
import { useOrgPromotionConfig } from '../../../../contexts/OrgPromotionConfigContext'; // hot snapshot + helpers
import { isOrgManagedDropdownInstance } from '../../../../utils/managedFieldInstances';

// Plain helper (no React hooks at module scope)
function inferPromotionKind(t) {
  if (t === 'multi-select')  return 'array'; // multi → arrayKeys
  if (t === 'single-select') return 'key';   // single → keys (explicit)
  if (t === 'date-picker')   return 'date';  // date → dateKeys
  return 'key';
}

const MemoizedTextField = React.memo(({ label, value, onChange, onBlur, onKeyDown, onFocus, onClose, type, helperText, options, disabled = false }) => {
  if (type === 'select') {
    return (
      <FormControl fullWidth margin="normal" sx={{ my: 2 }} disabled={disabled}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={value || ''} // Ensure that value is never undefined
          onChange={onChange}
         // IMPORTANT: prevent flush when the menu opens in a portal
         // Let the parent decide when to flush via onClose
         onBlur={undefined}
         onClose={onClose}
          displayEmpty
          disabled={disabled}
        >
          {options.map((option, index) => (
            <MenuItem key={index} value={option.value}>{option.label}</MenuItem>
          ))}
        </Select>
        <Typography variant="caption" sx={{ mt: 1 }}>{helperText}</Typography>
      </FormControl>
    );
  } else {
    return (
      <TextField
        fullWidth
        label={label}
        value={value || ''} // Ensure that value is never undefined
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        type={type}
        margin="normal"
        sx={{ my: 2 }}
        helperText={helperText}
        disabled={disabled}
      />
    );
  }
});


const BasicSettings = () => {
  const { selectedFieldForEdit, updateField } = useFieldEdit();
  const selectedFieldId = selectedFieldForEdit?.id || null; // safe/stable id

  const { formDetails } = useFormContext(); // + bring in current form details

  const isAssetUploadForm = formDetails?.isAssetUploadForm === true; // + AUF gate
  const { organizationId } = useAuth(); // + org scope for promotion
  const fieldUfi  = selectedFieldForEdit?.metadata?.ufi;   // + current field UFI
  const fieldType = selectedFieldForEdit?.metadata?.type;  // + (handy later)

  const selectedMetadata = selectedFieldForEdit?.metadata || null;
  const isOrgManagedField = isOrgManagedDropdownInstance(selectedMetadata);
  const managedGovernance = selectedMetadata?.governance || {};

  /**
   * BasicSettings governance policy
   * ---------------------------------------------------------------------------
   * SmartList-backed fields are centrally governed. This panel may display
   * their Basic settings for context, but it should not allow local changes
   * unless the managed field governance explicitly permits that override.
   *
   * IMPORTANT
   *  • This is a UI/write guard only.
   *  • SmartList option values remain governed by SmartLists.
   *  • Form-specific dependencies remain editable through the Dependencies tab.
   *  • Existing local form fields remain fully editable.
   */
  const allowLocalLabelOverride =
    !isOrgManagedField ||
    managedGovernance?.allowLocalLabelOverride === true ||
    selectedMetadata?.allowLocalLabelOverride === true;

  const allowLocalRequiredOverride =
    !isOrgManagedField ||
    managedGovernance?.allowLocalRequiredOverride === true ||
    selectedMetadata?.allowLocalRequiredOverride === true;

  const allowLocalPlaceholderOverride =
    !isOrgManagedField ||
    managedGovernance?.allowLocalPlaceholderOverride === true ||
    selectedMetadata?.allowLocalPlaceholderOverride === true;

  const allowLocalHelperTextOverride =
    !isOrgManagedField ||
    managedGovernance?.allowLocalHelperTextOverride === true ||
    selectedMetadata?.allowLocalHelperTextOverride === true;

  const isBasicSettingEditable = useCallback((prop) => {
    if (!isOrgManagedField) {
      return true;
    }

    switch (prop) {
      case 'label':
        return allowLocalLabelOverride;

      case 'required':
        return allowLocalRequiredOverride;

      case 'placeholder':
        return allowLocalPlaceholderOverride;

      case 'helperText':
        return allowLocalHelperTextOverride;

      default:
        return false;
    }
  }, [
    isOrgManagedField,
    allowLocalLabelOverride,
    allowLocalRequiredOverride,
    allowLocalPlaceholderOverride,
    allowLocalHelperTextOverride,
  ]);

  // Ndebug gate so keystrokes don't spam logs unless you opt-in.
  // Turn on in devtools: window.__DEBUG_BASIC_SETTINGS__ = true
  const DEBUG = Boolean(
    typeof window !== 'undefined' && window.__DEBUG_BASIC_SETTINGS__ === true
  );
  const dbg = useCallback((...args) => {
    if (!DEBUG) return;
    console.log(...args);
  }, [DEBUG]);

  // 🔎 DIAG (render): see what the switches will use on each render
useEffect(() => {
  dbg('[BasicSettings:render] switches', {
    fieldId  : selectedFieldForEdit?.id,
    required : selectedFieldForEdit?.metadata?.required,
    hidden   : selectedFieldForEdit?.metadata?.hidden,
    label    : selectedFieldForEdit?.metadata?.label
  });
}, [
  dbg,
  selectedFieldForEdit?.id,
  selectedFieldForEdit?.metadata?.required,
  selectedFieldForEdit?.metadata?.hidden,
  selectedFieldForEdit?.metadata?.label
]);


  // Log to confirm selectedFieldForEdit data upon component load
//  console.log('[BasicSettings: debug] Received selectedFieldForEdit:', selectedFieldForEdit);

  const [localValues, setLocalValues] = useState({});
  const [disabledDependency, setDisabledDependency] = useState(selectedFieldForEdit?.metadata?.disabledDependentFieldId || '');

 //  Helper function to remove any nested `transformations` keys,
  // and optionally remove top-level transform keys from the parent
  // that might overshadow nested ones.
  const flattenTransformations = (metadataObj) => {
    if (!metadataObj) return metadataObj;

    // Create a shallow clone so we don't mutate original
    const copy = { ...metadataObj };

    // If there's a top-level `transformations` object, remove
    // any accidentally duplicated transform keys from the parent.
    if (copy.transformations) {
      // Remove parent-level transform keys so only `copy.transformations` holds them
      delete copy.capitalize;
      delete copy.spacing;
      delete copy.encode;
      delete copy.allowSymbols;

      // Now, inside `copy.transformations`, remove any nested `transformations` property
      const { transformations: nested, ...rest } = copy.transformations;
      copy.transformations = rest; 
    }

    return copy;
  };

  /* ─────────────────────────────────────────────────────────────
   * Selective patch helpers – derive from UI instead of hard-coding
   * ──────────────────────────────────────────────────────────── */

  function getByPath(obj, path) {
    const keys = path.split('.');
    let cur = obj;
    for (const k of keys) {
      if (cur == null) return undefined;
      cur = cur[k];
    }
    return cur;
  }

  function setByPath(obj, path, value) {
    const keys = path.split('.');
    let cur = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      if (typeof cur[k] !== 'object' || cur[k] === null) cur[k] = {};
      cur = cur[k];
    }
    cur[keys[keys.length - 1]] = value;
  }

  // Build a patch object with only the settings this panel edits.
  // NOTE: editableProps will be derived from your UI `fields` array below.
  function buildEditablePatch({ local, latest, editableProps }) {
    const patch = {};
    for (const path of editableProps) {
      const localVal  = getByPath(local,  path);
      const latestVal = getByPath(latest, path);
      if (localVal !== undefined && localVal !== latestVal) {
        setByPath(patch, path, localVal);
      }
    }

    // ──────────────────────────────────────────────────────────
    // CRITICAL: don’t clobber the whole transformations object.
    // If we edited only 'transformations.capitalize' or '.spacing',
    // merge with the existing object so allowSymbols/encode survive.
    // ──────────────────────────────────────────────────────────
    if (patch.transformations) {
      patch.transformations = {
        ...(latest?.transformations || {}),
        ...patch.transformations,
      };
    }

    // Prevent partial nested patches from clobbering sibling keys
    // If we edited only blankValuePolicy.text, preserve mode as well.
    if (patch.blankValuePolicy) {
      patch.blankValuePolicy = {
        ...(latest?.blankValuePolicy || {}),
        ...patch.blankValuePolicy,
      };
    }

    return flattenTransformations(patch);
  }



// Track if user has typed; we’ll persist onBlur (and on unmount safety)
const [isDirty, setIsDirty] = useState(false);

// coalesce blur/unmount and guard against duplicate flushes
const flushedRef        = useRef(false);  // marks that we already flushed this edit session
const latestLocalRef   = useRef({});
const containerRef          = useRef(null);
const pendingBlurTimerRef   = useRef(null);

  // Detect when the currently edited field is a date-picker
  const isDatePicker = selectedFieldForEdit?.metadata?.type === 'date-picker';

  // Base field definitions for this panel
  const allFields = [
    { prop: 'label', label: 'Field Label', type: 'text' },
    { prop: 'placeholder', label: 'Placeholder', type: 'text' },
    { prop: 'helperText', label: 'Helper Text', type: 'text' },
    { prop: 'hint', label: 'Hint', type: 'text' },
    { prop: 'defaultValue', label: 'Default Value', type: 'text' },
    { prop: 'externalHelperText', label: 'External Error Message', type: 'text' },
    { prop: 'maxLength', label: 'Maximum Length', type: 'number', helperText: 'Set the maximum # of characters.' },
    {
      prop: 'transformations.capitalize',
      label: 'Capitalization Settings',
      type: 'select',
      helperText: 'Transform caps (ie. all CAPS).',
      options: [
        { label: 'No restrictions', value: 'none' },
        { label: 'ALL CAPS', value: 'all-caps' },
        { label: 'no caps', value: 'no-caps' },
        { label: 'First letter only', value: 'first-letter' }
      ]
    },
    {
      prop: 'transformations.spacing',
      label: 'Spacing Settings',
      type: 'select',
      helperText: 'Transform spacing (ie. as hyphens).',
      options: [
        { label: 'No restrictions', value: 'none' },
        { label: 'Underscore', value: 'underscore' },
        { label: 'Hyphen', value: 'hyphen' },
        { label: 'Remove spaces', value: 'no-spacing' }
      ]
    },
    {
      prop: 'disabledDependentFieldId',
      label: 'Disable Field When...',
      type: 'custom-select',
      helperText: 'Select a field that controls when this field is disabled.',
    }
  ];

  // For date-picker fields, let DatePickerSettings own date-specific transformations.
  // BasicSettings will still handle label, placeholder, helperText, defaultValue, etc.
  const fields = isDatePicker
    ? allFields.filter((f) => !f.prop.startsWith('transformations.'))
    : allFields;



  // Derive the list of editable props from the UI definition itself
  const editableProps = useMemo(() => {
    const fromTextInputs = fields
      .filter(f => f.type !== 'custom-select')     // custom-select handled by its own handler
      .filter(f => isBasicSettingEditable(f.prop))
      .map(f => f.prop);

    // Include the custom select prop as well so it’s included when we blur any text field
    // only when governance permits it.
    const optionalProps = [
      'disabledDependentFieldId',

      // injected “Blank value” field is not in allFields,
      // so we must explicitly include it or blur-flush will never persist it.
      'blankValuePolicy.text',
    ].filter((prop) => isBasicSettingEditable(prop));

    return [
      ...fromTextInputs,
      ...optionalProps,
    ];
  }, [
    fields,
    isBasicSettingEditable,
  ]);

  // 🔎 DIAG: confirm what BasicSettings will write on blur
useEffect(() => {
  dbg('[BasicSettings:diag] editableProps =', editableProps);
}, [dbg, editableProps]);



const flushLocalToContext = useCallback(() => {
  // Safety: if selection was cleared while typing, do nothing.
  if (!selectedFieldForEdit?.id || !selectedFieldForEdit?.metadata) return;
  if (!isDirty || flushedRef.current) return;

  if (editableProps.length === 0) {
    console.warn('[BasicSettings] Skipping Basic settings flush because no editable props are allowed by governance.', {
      fieldId: selectedFieldForEdit.id,
      isOrgManagedField,
    });

    flushedRef.current = true;
    setIsDirty(false);
    return;
  }

  const latest  = selectedFieldForEdit.metadata || {};
  const fromRef = latestLocalRef.current || {};

  // 🔎 DIAG (blur): show the current local snapshot and latest metadata
//  console.log('[BasicSettings:diag] blur local snapshot (fromRef)=', fromRef);
//  console.log('[BasicSettings:diag] blur latest (selectedFieldForEdit.metadata)=', latest);

  // Only include fields this panel edits (prevents re-toggling required/hidden)
  const patch = buildEditablePatch({
    local : fromRef,
    latest: latest,
    editableProps
  });

  // 🔎 DIAG: see how local differs from latest for the editableProps
  const diffReport = editableProps.map(p => ({
    path: p,
    latest: (p.split('.').reduce((o,k)=>o?.[k], latest)),
    local : (p.split('.').reduce((o,k)=>o?.[k], fromRef))
  }));
//  console.log('[BasicSettings:diag] blur: latest vs local (editableProps)', diffReport);


  if (Object.keys(patch).length === 0) {
    // nothing changed → do not flip other flags by accident
    flushedRef.current = true;
    setIsDirty(false);
    return;
  }

  // 🔎 DIAG: log exactly what we are writing
//  console.log('[BasicSettings:diag] blur PATCH keys =', Object.keys(patch),
//              '| patch=', patch);

//  console.log(
//    '[BasicSettings] FLUSH onBlur: helperText for',
//    selectedFieldForEdit.id,
//    '→',
//    patch.helperText
//  );


  flushedRef.current = true;
  updateField(selectedFieldForEdit.id, patch);
  setIsDirty(false);
}, [
  isDirty,
  selectedFieldForEdit,
  updateField,
  editableProps,
  isOrgManagedField,
]);


// Cancel any scheduled blur if the field immediately regains focus
const handleFieldFocus = useCallback(() => {
  if (pendingBlurTimerRef.current) {
    try { clearTimeout(pendingBlurTimerRef.current); } catch (_) {}
    pendingBlurTimerRef.current = null;
  }
}, []);

// Defer logic removed for inputs: we always commit on blur so edits persist
const handleFieldBlur = useCallback(() => {
  if (!isDirty || flushedRef.current) return;
  flushLocalToContext();  // immediate commit on input blur
}, [isDirty, flushLocalToContext]);


// Safety: if the panel unmounts while dirty, push once
useEffect(() => {
  return () => {
    if (pendingBlurTimerRef.current) {
      try { clearTimeout(pendingBlurTimerRef.current); } catch (_) {}
      pendingBlurTimerRef.current = null;
    }
    if (isDirty) {
      try { flushLocalToContext(); } catch (_) {}
    }
  };
}, [isDirty, flushLocalToContext]);


  // 1) Local optimistic flag
  const [libraryFilterEnabled, setLibraryFilterEnabled] = useState(false);
 // Org facets (live snapshot via provider)
 const {
   isReady,                       // authoritative readiness
   bucketOf: contextBucketOf,     // bucketOf(ufi) -> 'key'|'array'|'date'|null
   promoteUfi: promoteFromCtx,    // promoteUfi(ufi, kind)
   demoteUfi : demoteFromCtx      // demoteUfi(ufi)
 } = useOrgPromotionConfig() || {};

 // Derived, authoritative state for current field (prefer server snapshot when available)
 const derivedEnabled = useMemo(() => {
   if (!isAssetUploadForm || !isReady || !fieldUfi) return null; // indicates "unknown yet"
   const b = contextBucketOf(fieldUfi);
   return Boolean(b);
 }, [isAssetUploadForm, isReady, fieldUfi, contextBucketOf]);


  const handleToggleLibraryFilter = useCallback(async (event) => {
  if (!isBasicSettingEditable('libraryFilter')) {
    console.warn('[BasicSettings] Blocked library filter toggle for governed SmartList field.', {
      fieldId: selectedFieldForEdit?.id || null,
      fieldUfi,
    });
    return;
  }

  const next = Boolean(event.target?.checked);

  // 🔊 Diagnostics for the toggle path
//  console.log('[BasicSettings] toggle:onChange', {
//    next,
//    isAssetUploadForm,
//    organizationId,
//    selectedFieldId: selectedFieldForEdit?.id,
//    fieldUfi,
//    fieldType,
//  });

  // Optimistic local UI
  setLibraryFilterEnabled(next);

  if (!isAssetUploadForm || !organizationId || !fieldUfi) {
    if (!isAssetUploadForm) console.warn('[BasicSettings] toggle blocked: not an Asset Upload Form');
    if (!organizationId)     console.warn('[BasicSettings] toggle blocked: missing organizationId');
    if (!fieldUfi)           console.warn('[BasicSettings] toggle blocked: missing fieldUfi on selected field', { selectedFieldForEdit });
    return;
  }

  try {
    if (next) {
      const kind = inferPromotionKind(selectedFieldForEdit?.metadata?.type);
//      console.log('[BasicSettings] promoting UFI', { organizationId, fieldUfi, kind });
      await promoteFromCtx(fieldUfi, kind);   // provider handles write + optimistic reconcile
//      console.log('[BasicSettings] promoteUfi resolved');
    } else {
//      console.log('[BasicSettings] demoting UFI', { organizationId, fieldUfi });
      await demoteFromCtx(fieldUfi);          // provider handles write + optimistic reconcile
//      console.log('[BasicSettings] demoteUfi resolved');
    }
  } catch (e) {
    console.error('[BasicSettings] Promotion toggle failed (reverting):', e?.message || e);
    setLibraryFilterEnabled(!next); // rollback on failure
  }
}, [
  isAssetUploadForm,
  organizationId,
  fieldUfi,
  selectedFieldForEdit?.metadata?.type,
  selectedFieldForEdit?.id,
  fieldType,
  promoteFromCtx,
  demoteFromCtx,
  isBasicSettingEditable,
]);

 

  // Ensure that hooks are not conditionally used
useEffect(() => {
  if (selectedFieldForEdit?.id && selectedFieldForEdit.metadata) {
//      console.log("[BasicSettings] Initializing localValues with selectedFieldForEdit metadata:");
//      console.log("[BasicSettings] - Metadata:", selectedFieldForEdit.metadata);
//      console.log("[BasicSettings] - Transformations:", selectedFieldForEdit.metadata.transformations);

      // Keep your original approach, but flatten any existing transformations.
      const sanitizedMetadata = flattenTransformations(selectedFieldForEdit.metadata);

      // Merge your default fallback values into `sanitizedMetadata.transformations`
      // so you preserve the prior logic of "capitalize: none, spacing: none" etc.
      setLocalValues({
        ...sanitizedMetadata,
        transformations: {
          capitalize: 'none',
          spacing: 'none',
          allowSymbols: false,
          encode: false,
          ...(sanitizedMetadata.transformations || {}),
        },

        // blank handling policy (safe defaults)
        blankValuePolicy: {
          mode: 'empty',  // 'empty' | 'text' | 'null'
          text: 'null',
          ...(sanitizedMetadata.blankValuePolicy || {}),
        },
      });
      
       // Keep shadow state in sync on field switch (optional polish)
       latestLocalRef.current = {
         ...sanitizedMetadata,
         transformations: {
           capitalize: 'none',
           spacing: 'none',
           allowSymbols: false,
           encode: false,
           ...(sanitizedMetadata.transformations || {}),
         },

         // keep ref in sync too (used by blur flush)
         blankValuePolicy: {
           mode: 'empty',
           text: 'null',
           ...(sanitizedMetadata.blankValuePolicy || {}),
         },
       };      
      // Sync disabled dependency state
      setDisabledDependency(selectedFieldForEdit.metadata.disabledDependentFieldId || '');
      setIsDirty(false); // new field selection → not dirty yet
      flushedRef.current = false; // NEW: new field => no flush has happened yet
    }
 }, [selectedFieldForEdit?.id]);  // only when the selected field changes




const handleChange = useCallback(
  (prop, event) => {
    if (!isBasicSettingEditable(prop)) {
      console.warn('[BasicSettings] Blocked Basic settings edit for governed SmartList field.', {
        fieldId: selectedFieldForEdit?.id || null,
        prop,
      });
      return;
    }

    const newValue = event.target.value || '';

    dbg("[BasicSettings] handleChange triggered for:", prop);
    dbg("[BasicSettings] - New Value:", newValue);

    setLocalValues((prevValues) => {
      const keys = prop.split('.');
      let currentValues = { ...prevValues };
      let valueRef = currentValues;

      for (let i = 0; i < keys.length - 1; i++) {
        valueRef[keys[i]] = valueRef[keys[i]] || {};
        valueRef = valueRef[keys[i]];
      }

      const lastKey = keys[keys.length - 1];

      if (keys[0] === "transformations") {
        currentValues = flattenTransformations(currentValues);

        valueRef = currentValues;
        for (let i = 0; i < keys.length - 1; i++) {
          valueRef[keys[i]] = valueRef[keys[i]] || {};
          valueRef = valueRef[keys[i]];
        }

        valueRef[lastKey] = newValue;
      }
      else if (lastKey === 'maxLength') {
        valueRef[lastKey] = newValue ? Number(newValue) : 0;
      }
      else {
        valueRef[lastKey] = newValue;
      }

      dbg("[BasicSettings] Updated localValues (local-only):", currentValues);

      const finalMetadata = flattenTransformations(currentValues);

      dbg(
        '[BasicSettings] helperText leaving BasicSettings (local preview) for',
        selectedFieldId, //  use safe id
        '→',
        finalMetadata.helperText
      );

      const localKeys = Object.keys(finalMetadata);
      const localTransKeys = Object.keys(finalMetadata.transformations || {});
      dbg('[BasicSettings:diag] local snapshot keys=', localKeys,
          '| transformations keys=', localTransKeys);

      setIsDirty(true);
      flushedRef.current = false;
      latestLocalRef.current = finalMetadata;

      return { ...finalMetadata };
    });
  },
  [
    selectedFieldId,
    selectedFieldForEdit?.id,
    isBasicSettingEditable,
    dbg,
  ] 
);




  // Toggle Handlers
  const handleToggleRequired = useCallback((event) => {
    if (!isBasicSettingEditable('required')) {
      console.warn('[BasicSettings] Blocked required toggle for governed SmartList field.', {
        fieldId: selectedFieldForEdit?.id || null,
      });
      return;
    }

    if (selectedFieldForEdit?.metadata) {
      const updatedRequired = event.target.checked;
      console.log('[BasicSettings] required toggled for', selectedFieldForEdit.id, '→', updatedRequired);
      const prevVal = selectedFieldForEdit.metadata.required;
      console.log('[BasicSettings:diag] required prev=', prevVal);

      if (updatedRequired !== prevVal) {
        updateField(selectedFieldForEdit.id, { ...selectedFieldForEdit.metadata, required: updatedRequired });

        // 🔎 DIAG: read back after React commit
        requestAnimationFrame(() => {
          const readBack = document.querySelector('[data-basic-settings-required-probe]')?.getAttribute('data-probe');
          console.log('[BasicSettings:diag] required post-commit (RAF probe)=', readBack);
        });
      }
    }
  }, [
    selectedFieldForEdit,
    updateField,
    isBasicSettingEditable,
  ]);


  const handleToggleHidden = useCallback((event) => {
    if (selectedFieldForEdit?.metadata) {
      const updatedHidden = event.target.checked;
      if (updatedHidden !== selectedFieldForEdit.metadata.hidden) {
        updateField(selectedFieldForEdit.id, { ...selectedFieldForEdit.metadata, hidden: updatedHidden });
      }
    }
  }, [selectedFieldForEdit, updateField]);

  // Replace empty value toggle (replaces "Make hidden" UI behavior)
  const handleToggleBlankReplace = useCallback((event) => {
    if (!selectedFieldForEdit?.id) return;

    if (!isBasicSettingEditable('blankValuePolicy.text')) {
      console.warn('[BasicSettings] Blocked blank value policy edit for governed SmartList field.', {
        fieldId: selectedFieldForEdit?.id || null,
      });
      return;
    }

    const checked = Boolean(event.target?.checked);

    const nextPolicy = {
      mode: checked ? 'text' : 'empty',
      text: (localValues?.blankValuePolicy?.text ?? selectedFieldForEdit?.metadata?.blankValuePolicy?.text ?? 'null'),
    };

    // 1) Update local UI state immediately so the textfield enables/disables
    setLocalValues((prev) => {
      const next = {
        ...(prev || {}),
        blankValuePolicy: nextPolicy,
      };

      // Keep blur-flush ref coherent
      const finalMetadata = flattenTransformations(next);
      latestLocalRef.current = finalMetadata;

      // Mark dirty so a subsequent blur can flush if needed
      setIsDirty(true);
      flushedRef.current = false;

      return finalMetadata;
    });

    // 2) Persist to FieldEditContext immediately (like your other toggles)
    updateField(selectedFieldForEdit.id, { blankValuePolicy: nextPolicy });
  }, [
    selectedFieldForEdit?.id,
    updateField,
    localValues?.blankValuePolicy?.text,
    selectedFieldForEdit?.metadata?.blankValuePolicy?.text,
    isBasicSettingEditable,
  ]);

  const handleToggleAllowSymbols = useCallback((event) => {
    if (!selectedFieldForEdit?.id) return;

    if (!isBasicSettingEditable('transformations.allowSymbols')) {
      console.warn('[BasicSettings] Blocked allow-symbols edit for governed SmartList field.', {
        fieldId: selectedFieldForEdit?.id || null,
      });
      return;
    }

    const updatedAllowSymbols = event.target.checked;

    const existingTransformations = selectedFieldForEdit.metadata?.transformations || {};
    if (updatedAllowSymbols === existingTransformations.allowSymbols) return;

    const transformationsUpdate = {
      ...existingTransformations,
      allowSymbols: updatedAllowSymbols,
    };

    // Minimal patch: only transformations
    updateField(selectedFieldForEdit.id, { transformations: transformationsUpdate });
  }, [
    selectedFieldForEdit?.id,
    selectedFieldForEdit?.metadata?.transformations,
    updateField,
    isBasicSettingEditable,
  ]);


  const handleToggleEncode = useCallback((event) => {
    if (!selectedFieldForEdit?.id) return;

    if (!isBasicSettingEditable('transformations.encode')) {
      console.warn('[BasicSettings] Blocked encode edit for governed SmartList field.', {
        fieldId: selectedFieldForEdit?.id || null,
      });
      return;
    }

    const updatedEncode = event.target.checked;

    const existingTransformations = selectedFieldForEdit.metadata?.transformations || {};
    if (updatedEncode === existingTransformations.encode) return;

    const transformationsUpdate = {
      ...existingTransformations,
      encode: updatedEncode,
    };

    //  Minimal patch: only transformations
    updateField(selectedFieldForEdit.id, { transformations: transformationsUpdate });
  }, [
    selectedFieldForEdit?.id,
    selectedFieldForEdit?.metadata?.transformations,
    updateField,
    isBasicSettingEditable,
  ]);


  const handleDisabledDependencyChange = useCallback((selectedOption) => {
    if (!selectedFieldForEdit?.id) return;

    if (!isBasicSettingEditable('disabledDependentFieldId')) {
      console.warn('[BasicSettings] Blocked disabled-dependency edit for governed SmartList field.', {
        fieldId: selectedFieldForEdit?.id || null,
      });
      return;
    }

    const newDependency = selectedOption?.value || null;

    setDisabledDependency(newDependency);

    // Minimal patch: only the one property
    updateField(selectedFieldForEdit.id, { disabledDependentFieldId: newDependency });
  }, [
    selectedFieldForEdit?.id,
    updateField,
    isBasicSettingEditable,
  ]);


  // The switch's checked value prioritizes the authoritative config snapshot if present.
  // Fallback to the optimistic local value until the snapshot is loaded.
  const checkedForSwitch = (derivedEnabled !== null) ? derivedEnabled : libraryFilterEnabled;

// Disable the AUF switch until the provider delivers a snapshot, if no UFI,
// or when the current field is governed by SmartLists.
const disabledSwitch = isAssetUploadForm
  ? (!isReady || !fieldUfi || !isBasicSettingEditable('libraryFilter'))
  : false;

  // DIAG (render): AUF toggle computed value & sources
useEffect(() => {
  dbg('[BasicSettings:render] AUF switch', {
    derivedEnabled,
    libraryFilterEnabled,
    checkedForSwitch
  });
}, [dbg, derivedEnabled, libraryFilterEnabled, checkedForSwitch]);


  // NEW: stable handler map so MemoizedTextField can actually memoize.
  // This prevents re-rendering every input on every keystroke.
  const fieldHandlersByProp = useMemo(() => {
    const map = {};
    (fields || []).forEach((f) => {
      if (!f || f.type === 'custom-select') return;

      map[f.prop] = {
        onChange: (e) => handleChange(f.prop, e),
        onBlur:   (f.type === 'select') ? undefined : handleFieldBlur,
        onFocus:  (f.type === 'select') ? undefined : handleFieldFocus,
        onClose:  (f.type === 'select') ? flushLocalToContext : undefined,
        onKeyDown: (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            flushLocalToContext();
            e.currentTarget.blur();
          }
        },
      };
    });
    return map;
  }, [fields, handleChange, handleFieldBlur, handleFieldFocus, flushLocalToContext]);

  // Prevent early return before hooks
  if (!selectedFieldForEdit || !selectedFieldForEdit.metadata) {
    return <Typography>Select a field to edit</Typography>;
  }

  return (
    <Box ref={containerRef} sx={{ maxWidth: '90%', mx: 'auto' }}>
      {/* Display Field Type */}
      <Typography variant="body2" sx={{ ml: 1, mb: 1.5, mt: 1 }}>
        <span style={{ fontWeight: 'bold', color: '#003459' }}>Field Type:</span>
        <span style={{ fontWeight: 'light', color: 'text.secondary', marginLeft: '8px' }}>
          {selectedFieldForEdit?.metadata.type}
        </span>
      </Typography>

      {isOrgManagedField ? (
        <Alert
          severity="info"
          sx={{
            mb: 1.5,
            fontSize: '0.8rem',
          }}
        >
          This field is managed in SmartLists. Basic settings are read-only here unless an organization governance rule allows a local override.
        </Alert>
      ) : null}

      {/* Toggle for 'Mark field as required' */}
      <FormControlLabel
        control={
          <Switch
            checked={selectedFieldForEdit?.metadata.required || false}
            onChange={handleToggleRequired}
            name="requiredToggle"
            color="primary"
            size="small"
            disabled={!isBasicSettingEditable('required')}
          />
        }
        label={
          <Typography variant="body2" sx={{ fontWeight: 'light', color: 'text.secondary' }}>
            Make required
          </Typography>
        }
        sx={{ ml: 1, mb: 1 }}
      />

      {/* AUF-only vs non-AUF toggle slot */}
      {isAssetUploadForm ? (
        /* AUF: "Add as Library Filter" (authoritative config–driven with optimistic fallback) */
        <FormControlLabel
          control={
            <Tooltip title="Show this field in the Asset Library filter bar" arrow>
              <span>
                <Switch
                  checked={checkedForSwitch}
                  onChange={handleToggleLibraryFilter}
                  disabled={disabledSwitch}
                  name="libraryFilterToggle"
                  color="primary"
                  size="small"
                />
              </span>
            </Tooltip>
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 'light', color: 'text.secondary' }}>
              Add as Library Filter
            </Typography>
          }
          sx={{ ml: 1, mb: 1 }}
        />
      ) : (
        /* Non-AUF: Replace empty value (replaces old Make hidden toggle) */
        <FormControlLabel
          control={
            <Switch
              checked={(localValues?.blankValuePolicy?.mode || selectedFieldForEdit?.metadata?.blankValuePolicy?.mode || 'empty') !== 'empty'}
              onChange={handleToggleBlankReplace}
              name="blankReplaceToggle"
              color="primary"
              size="small"
              disabled={!isBasicSettingEditable('blankValuePolicy.text')}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 'light', color: 'text.secondary' }}>
              Replace empty value
            </Typography>
          }
          sx={{ ml: 1, mb: 1 }}
        />
      )}

      {/* Toggle for 'Allow symbols' */}
      <FormControlLabel
        control={
          <Switch
            checked={selectedFieldForEdit?.metadata.transformations?.allowSymbols || false}
            onChange={handleToggleAllowSymbols}
            name="allowSymbolsToggle"
            color="primary"
            size="small"
            disabled={!isBasicSettingEditable('transformations.allowSymbols')}
          />
        }
        label={
          <Typography variant="body2" sx={{ fontWeight: 'light', color: 'text.secondary' }}>
            Allow symbols
          </Typography>
        }
        sx={{ ml: 1, mb: 1 }}
      />

      {/* Toggle for 'Encode field upon submit' */}
      <FormControlLabel
        control={
          <Switch
            checked={selectedFieldForEdit?.metadata.transformations?.encode || false}
            onChange={handleToggleEncode}
            name="encodeToggle"
            color="primary"
            size="small"
            disabled={!isBasicSettingEditable('transformations.encode')}
          />
        }
        label={
          <Typography variant="body2" sx={{ fontWeight: 'light', color: 'text.secondary' }}>
            Encode upon submit
          </Typography>
        }
        sx={{ ml: 1, mb: 2 }}
      />

      {/* Render all form settings */}
      {fields.map(field => (
        field.type === 'custom-select' ? (
          // Custom component for "Disable Field Based On"
          <FormFieldCustomSelectComponent
            key={field.prop}
            value={disabledDependency || ''}  //  Ensure "No Dependency" option works
            onChange={handleDisabledDependencyChange}
            context="basic-settings" //  Explicitly define context
            disabled={!isBasicSettingEditable(field.prop)}
          />
        ) : (
          <React.Fragment key={field.prop}>
            <MemoizedTextField
              label={field.label}
              value={getValueForField(localValues, field.prop)}
              onChange={fieldHandlersByProp?.[field.prop]?.onChange}
              onBlur={fieldHandlersByProp?.[field.prop]?.onBlur}
              onFocus={fieldHandlersByProp?.[field.prop]?.onFocus}
              onClose={fieldHandlersByProp?.[field.prop]?.onClose}
              onKeyDown={fieldHandlersByProp?.[field.prop]?.onKeyDown}
              type={field.type}
              helperText={field.helperText}
              options={field.options || []}
              disabled={!isBasicSettingEditable(field.prop)}
            />

            {/* Always-visible Blank value input shown directly below Default Value */}
            {field.prop === 'defaultValue' && (
              <TextField
                fullWidth
                label="Blank value"
                value={getValueForField(localValues, 'blankValuePolicy.text')}
                onChange={(e) => handleChange('blankValuePolicy.text', e)}
                onBlur={handleFieldBlur}
                onFocus={handleFieldFocus}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    flushLocalToContext();
                    e.currentTarget.blur();
                  }
                }}
                margin="normal"
                sx={{ my: 2 }}
                disabled={
                  !isBasicSettingEditable('blankValuePolicy.text') ||
                  (localValues?.blankValuePolicy?.mode || selectedFieldForEdit?.metadata?.blankValuePolicy?.mode || 'empty') === 'empty'
                }
                helperText={
                  ((localValues?.blankValuePolicy?.mode || selectedFieldForEdit?.metadata?.blankValuePolicy?.mode || 'empty') === 'empty')
                    ? 'Enable “Replace empty value” to use this fallback when the user leaves the field blank.'
                    : 'Used only if the user submits this field empty.'
                }
              />
            )}
          </React.Fragment>
        )
      ))}

    </Box>
  );
};

// Keep your same getValueForField function
function getValueForField(values, propPath) {
  const keys = propPath.split('.');
  let result = values;
  for (let key of keys) {
    result = result[key] || '';
    if (result === '') break;
  }
  return result;
}

export default BasicSettings;
